/**
 * BatSystem — manages ambient bat spawning, lifecycle, attack triggers,
 * and Threia Totem protection.
 *
 * Responsibilities:
 *   - Roll spawn chance when the player reveals new fog-of-war cells
 *   - Create BatEntity instances at the edge of the fog reveal radius
 *   - Track active bats and update them each frame
 *   - Trigger the nearest bat to attack when the player picks up any item
 *   - Handle Threia Totem repel (blinding flash, bat flees)
 *   - Despawn bats that leave the player's visible area
 *   - Clean up on level change / scene shutdown
 */

import BatEntity from '../entities/BatEntity.js';
import { Events } from '../utils/constants.js';
import {
  BAT_SPAWN_CHANCE,
  BAT_DOUBLE_SPAWN_CHANCE,
  BAT_MAX_SPAWNS_PER_LEVEL,
  BAT_MIN_LEVEL,
  BAT_MIN_DISTANCE_TILES,
  BAT_REPEL_FLASH_DURATION,
  FOG_REVEAL_RADIUS,
  TILE_SIZE,
  AUDIO_BAT_REPEL,
} from '../config/game-config.js';
import { getSfxVol } from '../utils/audio-config.js';

export default class BatSystem {
  /**
   * @param {Phaser.Scene} scene         - The GameScene instance.
   * @param {Phaser.Events.EventEmitter} eventBus - Shared event bus.
   * @param {object} playerRef           - The Player sprite (has x, y, gridCol, gridRow).
   * @param {number[][]} visited         - Fog-of-war grid (0=unseen, 1=seen).
   * @param {number} mazeGridWidth       - Width of the maze grid.
   * @param {number} mazeGridHeight      - Height of the maze grid.
   * @param {number} level               - Current game level.
   * @param {() => boolean} hasTotemFn   - Returns true if player has Threia Totem.
   * @param {import('../systems/HealthSystem.js').default} healthSystem - HealthSystem reference.
   */
  constructor(scene, eventBus, playerRef, visited, mazeGridWidth, mazeGridHeight, level, hasTotemFn, healthSystem) {
    /** @type {Phaser.Scene} */
    this.scene = scene;
    /** @type {Phaser.Events.EventEmitter} */
    this._eventBus = eventBus;
    /** @type {object} */
    this._playerRef = playerRef;
    /** @type {number[][]} */
    this._visited = visited;
    /** @type {number} */
    this._mazeGridWidth = mazeGridWidth;
    /** @type {number} */
    this._mazeGridHeight = mazeGridHeight;
    /** @type {number} */
    this._level = level;
    /** @type {() => boolean} */
    this._hasTotemFn = hasTotemFn;
    /** @type {import('../systems/HealthSystem.js').default} */
    this._healthSystem = healthSystem;

    /** @type {BatEntity[]} Active bat entities. */
    this._bats = [];
    /** @type {number} Number of spawn events this level. */
    this._spawnCount = 0;
    /** @type {boolean} Whether an attack is currently in progress. */
    this._attackInProgress = false;

    // Listen for pickup events to trigger bat attacks
    this._pickupEvents = [
      Events.FOOD_COLLECTED,
      Events.COIN_COLLECTED,
      Events.KEY_COLLECTED,
      Events.COMPASS_COLLECTED,
      Events.THREIA_TOTEM_COLLECTED,
      Events.CHEST_OPENED,
      Events.AGMUTH_COLLECTED,
    ];

    this._onPickup = this._onPickup.bind(this);
    for (const evt of this._pickupEvents) {
      this._eventBus.on(evt, this._onPickup);
    }
  }

  // ---------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------

  /**
   * Called each frame from GameScene.update().
   * @param {number} _time
   * @param {number} delta
   */
  update(_time, delta) {
    if (!this.scene || !this._playerRef) return;

    // Update each active bat
    for (let i = this._bats.length - 1; i >= 0; i--) {
      const bat = this._bats[i];
      if (!bat.active) {
        this._bats.splice(i, 1);
        continue;
      }

      const result = bat.tick(delta);

      // Handle hit result
      if (result.hit) {
        this._onBatHit(bat, result.damage);
      }

      // Despawn if bat has left the visible area (beyond fog reveal radius * 2)
      if (bat.isActive) {
        const dx = bat.x - this._playerRef.x;
        const dy = bat.y - this._playerRef.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const maxVisibleDist = (FOG_REVEAL_RADIUS + 3) * TILE_SIZE;
        if (dist > maxVisibleDist) {
          bat.despawn();
        }
      }
    }
  }

  /**
   * Check whether a bat should spawn after the player reveals new cells.
   * Called from GameScene after _revealFog() on a PLAYER_MOVED event.
   *
   * @param {number} col - Player's current grid column.
   * @param {number} row - Player's current grid row.
   * @param {number} newCellsRevealed - Number of newly revealed cells this move.
   */
  trySpawn(col, row, newCellsRevealed) {
    if (this._level < BAT_MIN_LEVEL) return;
    if (this._spawnCount >= BAT_MAX_SPAWNS_PER_LEVEL) return;
    if (newCellsRevealed <= 0) return;

    // Roll spawn chance
    if (Math.random() >= BAT_SPAWN_CHANCE) return;

    this._spawnCount++;

    // Spawn first bat at edge of fog reveal radius
    const bat1 = this._spawnBatAtFogEdge(col, row);
    if (bat1) {
      this._bats.push(bat1);
      this._eventBus.emit(Events.BAT_SPAWNED);
    }

    // Roll for second bat
    if (Math.random() < BAT_DOUBLE_SPAWN_CHANCE) {
      const bat2 = this._spawnBatAtFogEdge(col, row);
      if (bat2) {
        this._bats.push(bat2);
        this._eventBus.emit(Events.BAT_SPAWNED);
      }
    }
  }

  /**
   * Destroy all active bats and clean up event listeners.
   */
  destroy() {
    // Remove pickup event listeners
    if (this._eventBus) {
      for (const evt of this._pickupEvents) {
        this._eventBus.off(evt, this._onPickup);
      }
    }

    // Destroy all active bats
    for (const bat of this._bats) {
      if (bat.active) {
        bat.destroy();
      }
    }
    this._bats = [];

    this.scene = null;
    this._playerRef = null;
    this._eventBus = null;
    this._healthSystem = null;
    this._hasTotemFn = null;
  }

  /** @returns {number} Number of currently active bats. */
  get activeBatCount() {
    return this._bats.filter(b => b.active).length;
  }

  // ---------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------

  /**
   * Spawn a bat at the edge of the player's fog reveal radius.
   * @private
   * @param {number} col - Player grid col.
   * @param {number} row - Player grid row.
   * @returns {BatEntity|null}
   */
  _spawnBatAtFogEdge(col, row) {
    if (!this.scene) return null;

    // Pick a random angle around the player and place at fog edge distance
    const angle = Math.random() * Math.PI * 2;
    const dist = (FOG_REVEAL_RADIUS + 0.5) * TILE_SIZE;
    const playerWorldX = col * TILE_SIZE + TILE_SIZE / 2;
    const playerWorldY = row * TILE_SIZE + TILE_SIZE / 2;
    const spawnX = playerWorldX + Math.cos(angle) * dist;
    const spawnY = playerWorldY + Math.sin(angle) * dist;

    return new BatEntity(this.scene, spawnX, spawnY, this._playerRef);
  }

  /**
   * Handle a pickup event — trigger the nearest flying bat to attack.
   * @private
   */
  _onPickup() {
    if (this._attackInProgress) return;
    if (!this._playerRef || !this._playerRef.active) return;

    // Find the nearest flying bat
    let nearest = null;
    let nearestDist = Infinity;

    for (const bat of this._bats) {
      if (!bat.active || bat.batState !== 'flying') continue;
      const dx = bat.x - this._playerRef.x;
      const dy = bat.y - this._playerRef.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = bat;
      }
    }

    if (!nearest) return;

    this._attackInProgress = true;

    // Check Threia Totem protection
    if (this._hasTotemFn && this._hasTotemFn()) {
      this._repelBat(nearest);
    } else {
      nearest.startAttack();
      this._eventBus.emit(Events.BAT_ATTACK);
    }
  }

  /**
   * Handle a bat hitting the player — apply damage.
   * @private
   * @param {BatEntity} bat
   * @param {number} damage
   */
  _onBatHit(bat, damage) {
    this._attackInProgress = false;

    if (this._healthSystem) {
      this._healthSystem.takeDamage(damage);
    }

    this._eventBus.emit(Events.BAT_HIT, damage);

    // Red flash on player to indicate damage
    if (this._playerRef && this._playerRef.playLifeLostEffect) {
      // Use a subtle red pulse — reuse the existing player method
      this._playerRef.setTint(0xff4444);
      if (this.scene) {
        this.scene.time.delayedCall(250, () => {
          if (this._playerRef && this._playerRef.active) {
            this._playerRef.clearTint();
          }
        });
      }
    }
  }

  /**
   * Repel a bat using the Threia Totem — blinding flash, bat flees.
   * @private
   * @param {BatEntity} bat
   */
  _repelBat(bat) {
    this._eventBus.emit(Events.BAT_REPELLED);

    // Play repel sound
    try {
      this.scene.sound.play(AUDIO_BAT_REPEL, { volume: getSfxVol(0.5) });
    } catch (_) { /* audio may be missing */ }

    // Blinding circular flash of white light from the player
    if (this.scene) {
      const px = this._playerRef.x;
      const py = this._playerRef.y;
      const radius = TILE_SIZE * 3;
      const alphaObj = { val: 0.9, scale: 1.0 };

      const flash = this.scene.add.graphics();
      flash.setDepth(250);

      const drawFlash = () => {
        flash.clear();
        const r = radius * alphaObj.scale;
        // Outer soft glow
        flash.fillStyle(0xffffff, alphaObj.val * 0.3);
        flash.fillCircle(px, py, r);
        // Mid ring
        flash.fillStyle(0xffffff, alphaObj.val * 0.6);
        flash.fillCircle(px, py, r * 0.6);
        // Inner bright core
        flash.fillStyle(0xffffff, alphaObj.val);
        flash.fillCircle(px, py, r * 0.3);
      };

      drawFlash();

      this.scene.tweens.add({
        targets: alphaObj,
        val: 0,
        scale: 1.5,
        duration: BAT_REPEL_FLASH_DURATION,
        ease: 'Quad.easeOut',
        onUpdate: drawFlash,
        onComplete: () => {
          flash.destroy();
        },
      });
    }

    // Bat flees
    bat.repel();

    // Reset attack lock after a brief delay
    if (this.scene) {
      this.scene.time.delayedCall(500, () => {
        this._attackInProgress = false;
      });
    }
  }
}
